// context/LayoutContext.js
import { useTheme } from '@mui/material'
import { useMediaQuery } from '@mui/system'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'

export type DockPosition = 'left' | 'right' | 'bottom' | null

type LayoutCtx = {
  pinned: boolean
  togglePinned: () => void
  navOpen: boolean
  setNavOpenState: (newValue: boolean) => void
  expanded?: boolean
  // Agent chat docking
  agentDock: DockPosition
  setAgentDock: (pos: DockPosition) => void
  agentDockSize: number
  setAgentDockSize: (size: number | ((prev: number) => number)) => void
  isAgentDockOpen: boolean
  setIsAgentDockOpen: (open: boolean) => void
  // Responsive breakpoints
  isMobile: boolean   // below md (<900px)
  isTablet: boolean   // md to lg (900px–1200px)
  isDesktop: boolean  // lg+ (≥1200px)
}

const SIDE_SIZE_DESKTOP = 420
const SIDE_SIZE_TABLET = 340
const DEFAULT_BOTTOM_SIZE = 350

const LayoutContext = createContext<LayoutCtx | null>(null)

/** Access the pinned state from your children */
export const useLayoutContext = () => useContext(LayoutContext)

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const theme = useTheme()
  const [pinned, setPinned] = useState<boolean>(true)
  const [navOpen, setNavOpen] = useState<boolean>(false)

  // Agent dock state
  const [agentDock, setAgentDockState] = useState<DockPosition>(null)
  const [agentDockSize, setAgentDockSizeState] = useState<number>(SIDE_SIZE_DESKTOP)
  const [isAgentDockOpen, setIsAgentDockOpenState] = useState<boolean>(false)
  const mdUp = useMediaQuery(theme.breakpoints.up('md'))
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'))
  const isMobile = !mdUp                 // below 900px
  const isTablet = mdUp && !lgUp         // 900px–1200px
  const isDesktop = lgUp                 // 1200px+

  const setNavOpenState = newValue => {
    setNavOpen(newValue)
  }

  // Load from localStorage on mount
  useEffect(() => {
    const storedPinned = localStorage.getItem('navPinned')
    if (storedPinned === null) {
      localStorage.setItem('navPinned', 'true')
      setPinned(true)
    } else {
      setPinned(storedPinned === 'true')
    }

    // Load dock state
    const storedDock = localStorage.getItem('agentDockPosition') as DockPosition
    const storedSize = localStorage.getItem('agentDockSize')
    if (storedDock) {
      setAgentDockState(storedDock)
      setIsAgentDockOpenState(true)
    }
    if (storedSize) {
      setAgentDockSizeState(Number(storedSize) || SIDE_SIZE_DESKTOP)
    }
  }, [])

  const togglePinned = () => {
    setPinned(prev => {
      const newPinnedState = !prev
      localStorage.setItem('navPinned', newPinnedState.toString())
      return newPinnedState
    })
  }

  const defaultSideSize = lgUp ? SIDE_SIZE_DESKTOP : SIDE_SIZE_TABLET

  const setAgentDock = useCallback((pos: DockPosition) => {
    setAgentDockState(pos)
    if (pos) {
      localStorage.setItem('agentDockPosition', pos)
      setIsAgentDockOpenState(true)
      // Reset size to default when switching dock positions
      const defaultSize = pos === 'bottom' ? DEFAULT_BOTTOM_SIZE : defaultSideSize
      setAgentDockSizeState(defaultSize)
      localStorage.setItem('agentDockSize', String(defaultSize))
    } else {
      localStorage.removeItem('agentDockPosition')
      setIsAgentDockOpenState(false)
    }
  }, [defaultSideSize])

  const setAgentDockSize = useCallback((size: number | ((prev: number) => number)) => {
    setAgentDockSizeState(prev => {
      const next = typeof size === 'function' ? size(prev) : size
      localStorage.setItem('agentDockSize', String(next))
      return next
    })
  }, [])

  const setIsAgentDockOpen = useCallback((open: boolean) => {
    setIsAgentDockOpenState(open)
  }, [])

  return (
    <LayoutContext.Provider
      value={{
        pinned,
        togglePinned,
        navOpen,
        setNavOpenState,
        agentDock,
        setAgentDock,
        agentDockSize,
        setAgentDockSize,
        isAgentDockOpen,
        setIsAgentDockOpen,
        isMobile,
        isTablet,
        isDesktop,
      }}
    >
      {children}
    </LayoutContext.Provider>
  )
}
